import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { AvatarList } from '@calendly/ui/components/avatar-list';
import { FullStoryExclude, FullStoryMask } from '@calendly/ui/components/fullstory-privacy';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { isEmpty, uniqBy } from 'lodash-es';
import { forwardRef, useMemo } from 'react';
import { useGetUserQuery } from 'common';
import { getMemberName } from 'components/event_type_edit';
import { Layout } from 'components/layout';
import { useEventType } from 'features/event_type_provider';
import { useDistinctEventTypeProfiles } from 'hooks/useDistinctEventTypeProfiles';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { filterEnabledMemberships } from '../../helpers';
import { WarningMessage } from '../../warning_message';
const avatarsContainerCss = "a1qlqvde";
const AvatarsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(avatarsContainerCss, props.className)} />;
});
const getHostsText = ({
  memberships,
  currentUser,
  isOnlyRoundRobinPool
}) => {
  if (isOnlyRoundRobinPool) {
    return I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.round_robin_summary', {
      count: memberships.length
    });
  }
  let hostsToDisplay = memberships.filter(membership => membership.user_uuid === currentUser.uuid);
  hostsToDisplay = hostsToDisplay.concat(memberships.filter(membership => !!membership.user_uuid));
  hostsToDisplay = hostsToDisplay.concat(memberships.slice(0, 2));
  hostsToDisplay = uniqBy(hostsToDisplay, 'user_uuid').slice(0, 2);
  const hostsText = hostsToDisplay.map(membership => membership.user_uuid ? getMemberName(membership, currentUser) : membership.name).join(', ');
  const otherMembersCount = memberships.length - hostsToDisplay.length;
  if (otherMembersCount === 0) {
    return hostsText;
  }
  return I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.members_summary_v4', {
    name: hostsText,
    count: otherMembersCount
  });
};
export const Summary = () => {
  const {
    id,
    isTeam
  } = useEventType();
  const {
    values: {
      memberships: formMemberships,
      userPools = []
    }
  } = useFormContext();
  const {
    isRoundRobin,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const isOnlyRoundRobinPool = useMemo(() => {
    if (!isRoundRobin && !isCollectiveRoundRobin) {
      return false;
    }
    if (isRoundRobin) {
      return true;
    }
    if (userPools.length === 1 && userPools[0].pooling_type === 'round_robin') {
      return true;
    }
    return false;
  }, [isCollectiveRoundRobin, isRoundRobin, userPools]);
  const memberships = id || isTeam ? filterEnabledMemberships(formMemberships) : formMemberships;
  const {
    profiles
  } = useDistinctEventTypeProfiles({
    memberships
  });
  const {
    data: currentUser
  } = useGetUserQuery();
  const areOnlyPendingUsers = memberships.length && memberships.every(m => m.is_pending || m.isPending);
  if (areOnlyPendingUsers) {
    return <WarningMessage message={I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.no_hosts_only_pending', {
      count: memberships.length
    })} />;
  }
  if (isEmpty(memberships)) {
    return <WarningMessage message={I18n.t('publisher.event_type_editor.sections.hosts.errors.no_hosts')} />;
  }
  return <Layout gap="8">
      <AvatarsContainer>
        <FullStoryExclude>
          <AvatarList profiles={profiles} limit={1} size="x-small" overlapped spacingRight={4} />
        </FullStoryExclude>
      </AvatarsContainer>
      <BodyText color={colorTextSecondary} size="medium" inline>
        <FullStoryMask>
          {getHostsText({
          memberships,
          currentUser,
          isOnlyRoundRobinPool
        })}
        </FullStoryMask>
      </BodyText>
    </Layout>;
};

require("./summary.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./summary.js");